/* eslint-disable array-callback-return */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import {
	ALS_ERLEDIGT_MARKIEREN_LABEL,
	ERLAUTERUNG_IN_KOSTEN_TITLE,
	FORM_FIELDS_TYPES,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
} from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue, GetMinMaxIntValidation, NonRequired } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getBasisDataKostenKategorie,
	getKostenErlauterung,
} from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { checkIfSpecificKostenPositionHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { withKostenkategorieEinsatzbereichFilter } from 'src/utils/einsatzbereichHelper';
import { mapindexbyPath } from 'src/utils/formStruc';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, getFieldNamefromDataPath, isTrue, removeDataKeys, removeNullDeep } from 'src/utils/helper';

// import schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = Schema.attributes;
const KP_P = KostenPositionSchema.attributes;

const PersonalKostenAntragLoewe: FC<{ kostentraeger: string; istHochschule: boolean; unternehmenId?: number; hasComment?: boolean }> = ({
	kostentraeger,
	istHochschule,
	unternehmenId,
	hasComment,
}) => {
	const { application, setApplicationProperty, readOnly, saveApplication } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const [kostenKategorie, setKostenKategorie] = useState([]);

	// Handles Add or Delte Kostenposition
	const handleAddPersonalkosten = async () => {
		let appCopy = cloneDeep(application);

		const data = getBasisDataKostenKategorie(
			appCopy,
			kostenKategorie, // All kostenkategorie for Personalkosten, to find default Kostenkategorie id
			kostentraeger,
			unternehmenId,
			KOSTEN_POSITION.Ausgabe,
			KOSTEN_ARTEN.Personalkosten,
		);
		set(data, getFieldNamefromDataPath(KP_P.Personalausgaben_LOEWE_Antrag.path), {
			[getFieldNamefromDataPath(KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Loewe_Antrag_Projekt_Jahr.path)]: Array.from({
				length: get(appCopy, P.Allgemein_Projektdauerin_Jahren.path, 0),
			}).map((_, i) => {
				return {
					Zelle_1: parseInt(get(appCopy, P.Allgemein_Projektstart_Jahr.path)) + i,
				};
			}),
		});
		set(
			appCopy,
			addIndexesToPath(
				P.Foerderantrag_Kostenpositions.path,
				[`${get(application, P.Foerderantrag_Kostenpositions.path, []).length}`],
				['Foerderantrag_Kostenpositions'],
			),
			{ attributes: data },
		);
		saveApplication(false, appCopy);
	};
	const handleDeletePersonalKosten = async (id: number) => {
		try {
			let appcopy = cloneDeep(application);
			const allKostenposition = get(appcopy, P.Foerderantrag_Kostenpositions.path, []);
			set(
				appcopy,
				P.Foerderantrag_Kostenpositions.path,
				allKostenposition.filter((item: any) => item.id !== id),
			);
			let res = await DeleteRecord(APIS.Kostenposition, id);
			if (res) {
				saveApplication(false, appcopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Personalkosten', err);
		}
	};

	// Fetch KostenKategories
	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = withKostenkategorieEinsatzbereichFilter(
					{
						Allgemein_Kostenposition: 'Ausgabe',
						Allgemein_Kostenart_Finanzierung: 'Personalkosten',
						Allgemein_Antragsformular: { $contains: get(application, P.Allgemein_Antragsformular.path) },
						Allgemein_Aktiv: { $eq: true },
					},
					APIS.Antrag,
				);
				let res = await GetRecords(APIS.Kostenkategorie, [], filter);
				setKostenKategorie(removeNullDeep(removeDataKeys(get(res, 'data'))));
			} catch (err: any) {
				console.error('ERROR: Fetching Kostenkategorie ', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{get(application, P.Foerderantrag_Kostenpositions.path, []).map((ele: any, i: number) => {
				if (
					get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
					get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Personalkosten &&
					get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId &&
					!isTrue(get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Administrationspauschale.path))
					// Administrationspauschale ist die 20% Pauschale für HAW, die im Akkordion nicht angezeigt werden müssen.
					// Das wird per Formel ausgerechnet bzw. angelegt, wenn die Antragstellende ein HAW ist.
				) {
					return (
						<MUIAccordionComponent
							key={i}
							summaryTitle={`Personalausgaben | ${get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Name_Desder_Beschaeftigten.path, '')}`}
							onDelete={() => handleDeletePersonalKosten(get(ele, 'id'))}
							showDeleteButton={!updatedReadOnly}
							hasComment={checkIfSpecificKostenPositionHasComment(readOnly, ele)}
						>
							<PruefAssistenz
								statusPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Pruefstatus.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								hinweisPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Hinweis_Fuer_Antragsteller.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								ueberarbeitetAmPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								erledigtPath={addIndexesToPath(
									P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt.path,
									[`${i}`],
									['Foerderantrag_Kostenpositions'],
								)}
								formStruc={mapPersonalKosten(PruefAssistenzStruc(), i)}
							/>

							<MUIAlert
								title={ERLAUTERUNG_IN_KOSTEN_TITLE}
								message={getKostenErlauterung(kostenKategorie, get(ele, KP_P.Allgemein_Kostenkategorie.path))}
							/>

							<CreateForm
								formStruc={mapPersonalKosten(formStruc1(application, istHochschule), i)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
								api={APIS.Kostenposition}
								size='small'
							/>
							<Box sx={{ mt: '2rem' }}>
								<CreateForm
									formStruc={mapPersonalKosten(KalkulationBlockStruc(application, istHochschule, i), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={updatedReadOnly}
									api={APIS.Kostenposition}
									size='small'
								/>
							</Box>
							<Box sx={{ mt: '2rem', mb: '2rem' }}>
								<CreateForm
									formStruc={mapPersonalKosten(formStrucKalkulationBerechnung(application, istHochschule, i), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={updatedReadOnly}
									api={APIS.Kostenposition}
									size='small'
								/>
							</Box>
						</MUIAccordionComponent>
					);
				}
			})}
			{unternehmenId ?
				<Button sx={{ mt: '2rem' }} endIcon={<AddIcon />} onClick={handleAddPersonalkosten} disabled={updatedReadOnly}>
					Personalkosten hinzufügen
				</Button>
			:	<MUIAlert message='Für diesen Partner wurden noch keine Daten ausgefüllt. Bitte tragen Sie die Partnerdaten zuerst ein.' />}
		</>
	);
};
export default PersonalKostenAntragLoewe;

const PruefAssistenzStruc = () => {
	return [
		{
			fields: [
				{
					...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
};

const formStruc1 = (_application: any, istHochschule: boolean) => {
	let struc: FormStrucItem[] = [];

	if (istHochschule) {
		struc = [
			{
				fields: [
					{
						...P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Name_Desder_Beschaeftigten,
						label: 'Name',
						tooltip: 'Personen können anonymisiert mit N.N. 1 etc. angegeben werden.',
					},
					P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Funktion_Im_Beantragten_Vorhaben,
				],
				userProps: { columnToDivide: 2 },
			},
			{
				fields: [
					{
						...P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Grundlage_Des_Stundensatzes,
						label: 'Grundlage des Stundensatzes',
						tooltip: 'Zum Beispiel: TV-H Eingruppierung, Haustarif, o.Ä.)',
						validation: [{ validateFunction: NonRequired }],
					},
				],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
		];
	} else {
		struc = [
			{
				fields: [
					{
						...P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Name_Desder_Beschaeftigten,
						label: 'Name',
						tooltip: 'Personen können anonymisiert mit N.N. 1 etc. angegeben werden.',
					},
					P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Funktion_Im_Beantragten_Vorhaben,
				],
				userProps: { columnToDivide: 2 },
			},
		];
	}
	return struc;
};
const KalkulationBlockStruc = (application: any, istHochschule: boolean, i: number) => {
	let struc: FormStrucItem[] = [];

	if (istHochschule) {
		struc = [
			{
				fields: [
					{
						...P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Loewe_Antrag_Projekt_Jahr,
						type: FORM_FIELDS_TYPES.editableTable,
						tableLabel: 'Bitte geben Sie hier die entsprechenden Daten ein:',
						disabledColumns: ['Zelle_1', 'Zelle_3'],
						hideColumns: ['Zelle_4', 'Zelle_5', 'Zelle_6', 'Zelle_8', 'Zelle_7', 'Zelle_9', 'Zelle_10'],
						fieldProps: { Zelle_4: { format: 'currency' }, Zelle_3: { format: 'currency' } },
						maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
						hideDeleteButton: true,
						sumRow: [
							{
								id: 'pinnedRow',
								Zelle_1: 'Gesamt',
								Zelle_2: get(
									application,
									addIndexesToPath(
										P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes
											.Personalausgaben_LOEWE_F_0206598338_Projektjahr_Block_2_Zelle_2.path,
										[`${i}`],
										['Foerderantrag_Kostenpositions'],
									),
								),
								Zelle_3: get(
									application,
									addIndexesToPath(
										P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes
											.Personalausgaben_LOEWE_F_0206598339_Projektjahr_Block_2_Zelle_3.path,
										[`${i}`],
										['Foerderantrag_Kostenpositions'],
									),
								),
							},
						],
					},
				],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
		];
	} else {
		struc = [
			{
				fields: [
					{
						...P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Loewe_Antrag_Projekt_Jahr,
						type: FORM_FIELDS_TYPES.editableTable,
						tableLabel: 'Bitte geben Sie hier die entsprechenden Daten ein:',
						disabledColumns: ['Zelle_1', 'Zelle_3'],
						hideColumns: ['Zelle_7', 'Zelle_8', 'Zelle_9', 'Zelle_10', 'Zelle_11'],
						fieldProps: { Zelle_4: { format: 'currency' }, Zelle_3: { format: 'currency' } },
						maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
						hideDeleteButton: true,
						sumRow: [
							{
								id: 'pinnedRow',
								Zelle_1: 'Gesamt',
								Zelle_2: get(
									application,
									addIndexesToPath(
										P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes
											.Personalausgaben_LOEWE_F_0206598338_Projektjahr_Block_2_Zelle_2.path,
										[`${i}`],
										['Foerderantrag_Kostenpositions'],
									),
								),
								Zelle_3: get(
									application,
									addIndexesToPath(
										P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes
											.Personalausgaben_LOEWE_F_0206598339_Projektjahr_Block_2_Zelle_3.path,
										[`${i}`],
										['Foerderantrag_Kostenpositions'],
									),
								),
							},
						],
					},
				],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
		];
	}
	return struc;
};
const formStrucKalkulationBerechnung = (application: any, istHochschule: boolean, _i: number) => {
	let struc: FormStrucItem[] = [];

	if (istHochschule) {
	} else {
		struc = [
			{
				fields: [
					{
						...P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Loewe_Antrag_Projekt_Jahr,
						type: FORM_FIELDS_TYPES.editableTable,
						tableLabel: 'Diese Bereiche errechnen sich nach Ihren oben gemachten Angaben:',
						disabledColumns: ['Zelle_1', 'Zelle_7', 'Zelle_8', 'Zelle_9', 'Zelle_10', 'Zelle_11', 'Zelle_3'],
						hideColumns: ['Zelle_4', 'Zelle_5', 'Zelle_6', 'Zelle_2'],
						fieldProps: { Zelle_7: { format: 'currency' }, Zelle_3: { format: 'currency' } },
						maxLevel: get(application, P.Allgemein_Projektdauerin_Jahren.path),
						hideDeleteButton: true,
					},
				],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
		];
	}
	return struc;
};

const mapPersonalKosten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Foerderantrag_Kostenpositions.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Foerderantrag_Kostenpositions.path.length),
				};
			}),
		};
	});
	return result;
};

const customFormStrucKalkulationBlock = (_application: any, istHochschule: boolean, _i: number) => {
	let struc: any[] = [];

	if (istHochschule) {
		struc = [];
	} else {
		struc = [
			{
				fields: [
					{
						key: 'Zelle_5',
						...P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Loewe_Antrag_Projekt_Jahr
							.schema.attributes.Zelle_5,
						validation: [{ validateFunction: GetMinMaxIntValidation, onlyValidateOnNonEmptyValue: true, params: [0, 12] }],
					},
					{
						key: 'Zelle_6',
						...P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Loewe_Antrag_Projekt_Jahr
							.schema.attributes.Zelle_6,
						validation: [{ validateFunction: GetMinMaxIntValidation, onlyValidateOnNonEmptyValue: true, params: [0, 40] }],
					},
				],
			},
		];
	}

	return struc;
};

export function getFromStrucPersonalKostenAntragLoewe(application: any, istHochschule: boolean, unternehmenId?: number): FormStrucItem[] {
	let formStruc: FormStrucItem[] = [];

	get(application, P.Foerderantrag_Kostenpositions.path, []).forEach((ele: any, i: number) => {
		if (
			get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
			get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Personalkosten &&
			get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId &&
			!isTrue(get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Administrationspauschale.path))
		) {
			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(ele, KP_P.Pruefung_Pruefstatus.path, KP_P.Pruefung_Erledigt.path);

			const formStruc1WithIndex = mapPersonalKosten(formStruc1(application, istHochschule), i);
			// const formStrucCT2WithIndex = getFormStrucComponentTable(
			// 	application,
			// 	mapPersonalKosten(KalkulationBlockStruc(application, istHochschule, i), i),
			// );

			const kalkulationBlocklength = get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Loewe_Antrag_Projekt_Jahr.path, []).length;
			let formStrucCustomKalkulationBlockWithIndex: any[] = [];

			for (let j = 0; j < kalkulationBlocklength; j++) {
				const customFormStrucWithIndex = mapindexbyPath(
					customFormStrucKalkulationBlock(application, istHochschule, i),
					j,
					P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.schema.attributes.Loewe_Antrag_Projekt_Jahr,
				);
				formStrucCustomKalkulationBlockWithIndex = concat(
					formStrucCustomKalkulationBlockWithIndex,
					mapPersonalKosten(customFormStrucWithIndex, i),
				);
			}

			if (isHinweisFieldRequired) {
				formStruc = concat(mapPersonalKosten(PruefAssistenzStruc(), i), formStruc, formStruc1WithIndex, formStrucCustomKalkulationBlockWithIndex);
			} else {
				formStruc = concat(formStruc, formStruc1WithIndex, formStrucCustomKalkulationBlockWithIndex);
			}
		}
	});
	return formStruc;
}
